import type { OAuthApp, TokenSet } from '../oauth';

/**
 * Everything a storage backend has to do.
 *
 * An envelope is one self-contained encrypted file, so this is the whole
 * surface: write it, read it back, remove it. Nothing here knows anything about
 * crypto, because the file is sealed long before storage sees it.
 */
export interface StorageBackend {
  put(path: string, body: Uint8Array): Promise<{ size: number }>;
  get(path: string): Promise<Uint8Array | null>;
  remove(path: string): Promise<void>;
}

export type ProviderId = 'dropbox' | 'googledrive' | 'onedrive' | 'r2';

export type AuthKind = 'oauth' | 'keys';

export interface ProviderMeta {
  id: ProviderId;
  name: string;
  /** One line, written for the person choosing, not the person implementing. */
  blurb: string;
  free: string;
  auth: AuthKind;
  /**
   * Whether the connection survives being left alone.
   *
   * This is the property that decides whether a provider can back an unattended
   * dead-man's switch later, so it is stated per provider rather than assumed.
   */
  durability: {
    lasts: 'indefinite' | 'months' | 'session';
    note: string;
  };
  /** Present for OAuth providers; absent for key-based ones. */
  app?: OAuthApp;
  /** True when an OAuth provider has no client id compiled in yet. */
  needsSetup?: boolean;
}

/** A live, usable connection. */
export interface Connection {
  provider: ProviderId;
  /** OAuth providers. */
  tokens?: TokenSet;
  /** Key-based providers (R2). */
  keys?: Record<string, string>;
  label?: string;
  verifiedAt?: number;
}

export interface TestStep {
  label: string;
  ok: boolean;
  detail?: string;
}

export interface TestResult {
  ok: boolean;
  steps: TestStep[];
  /** Set when the browser blocked us before the request left. */
  corsBlocked?: boolean;
}

/**
 * Prove a backend really works before anything is trusted to it.
 *
 * Writes a probe, reads it back, compares the bytes, deletes it. The comparison
 * matters as much as the status codes: a write that succeeds but reads back
 * wrong is worse than one that fails outright, because it looks like success.
 */
export async function runStorageTest(backend: StorageBackend): Promise<TestResult> {
  const steps: TestStep[] = [];
  const path = `probe-${Date.now()}.txt`;
  const payload = new TextEncoder().encode(`probe ${new Date().toISOString()}`);

  const step = async (label: string, fn: () => Promise<string | undefined>) => {
    try {
      steps.push({ label, ok: true, detail: await fn() });
      return true;
    } catch (e) {
      steps.push({ label, ok: false, detail: e instanceof Error ? e.message : String(e) });
      return false;
    }
  };

  if (!(await step('Write a test file', async () => {
    const { size } = await backend.put(path, payload);
    return `${size} bytes`;
  }))) {
    return { ok: false, steps };
  }

  const readOk = await step('Read it back', async () => {
    const got = await backend.get(path);
    if (!got) throw new Error('Written, but could not be read back.');
    const same =
      got.byteLength === payload.byteLength && got.every((b, i) => b === payload[i]);
    if (!same) throw new Error('What came back did not match what was written.');
    return 'contents match';
  });

  // Always attempt cleanup, even if the read failed — a failed test should not
  // leave litter in someone's storage.
  const cleanOk = await step('Remove the test file', async () => {
    await backend.remove(path);
    return undefined;
  });

  return { ok: readOk && cleanOk, steps };
}
